import * as React from 'react';
import { AuthStatus } from 'constants/auth';
import { useAuthModals } from 'hooks/useAuthModals';
import { useViewer } from 'hooks/useViewer';
import BounceLogoSplash from 'svg/BounceLogoSplash';
import { Button, ButtonText } from 'components/Button';

const boldTextClassName =
  'font-semibold text-color-text-lightmode-primary dark:text-color-text-darkmode-primary';

const AUTO_OPEN_DELAY_MS = 10_000;

// Module-level so the modal auto-opens at most once per session — the
// component remounts on every client-side navigation between pages.
let hasAutoOpened = false;

/**
 * Sign-up nudge for anonymous visitors: a persistent bottom-left toast plus
 * the create-account modal, which auto-opens once per session 10s after
 * landing on a page that renders this component (all viewports). Rendered by
 * the marketplace index pages — detail pages (e.g. /lessons/[lessonId]) are
 * intentionally excluded. The toast itself is hidden below `lg` since mobile
 * has its own bottom `TabBar` occupying that space.
 */
export default function CreateAccountToast() {
  const viewer = useViewer();
  const { ModalLogin, openLoginModal, ModalCreateAccountReminder, openCreateAccountReminderModal } =
    useAuthModals();
  const isAnon = viewer.status === AuthStatus.Anonymous;

  React.useEffect(() => {
    if (!isAnon || hasAutoOpened) {
      return;
    }
    const timer = setTimeout(() => {
      hasAutoOpened = true;
      openCreateAccountReminderModal();
    }, AUTO_OPEN_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isAnon]);

  if (!isAnon) {
    return null;
  }

  return (
    <>
      <div className="fixed bottom-8 left-8 z-30 hidden items-center rounded-3xl bg-color-bg-lightmode-primary px-6 py-4 shadow-[0_8px_24px_0_rgba(0,0,0,0.25)] dark:bg-color-bg-darkmode-primary lg:flex">
        <BounceLogoSplash className="mr-4 h-12 w-auto shrink-0" aria-label="Bounce" />
        <div>
          <p className="typography-product-subheading whitespace-nowrap text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            Sign Up for Bounce. It&apos;s free!
          </p>
          <p className="typography-product-text-card-desktop mt-1 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            <span className="block whitespace-nowrap">
              Get personalized <span className={boldTextClassName}>recommendations</span>,{' '}
              <span className={boldTextClassName}>discounts</span>,
            </span>
            <span className="block whitespace-nowrap">
              and <span className={boldTextClassName}>weekly updates</span> for what&apos;s
              happening in your city.
            </span>
          </p>
        </div>
        <div className="ml-3 flex shrink-0 flex-col items-center gap-3">
          <Button
            variant="brand"
            size="sm"
            isInline
            onClick={() => openCreateAccountReminderModal()}
          >
            Sign Up
          </Button>
          <ButtonText
            size="sm"
            onClick={() => openLoginModal()}
            className="text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
          >
            Log In
          </ButtonText>
        </div>
      </div>
      <ModalCreateAccountReminder />
      <ModalLogin isShowSignupLink={false} />
    </>
  );
}
